import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Alert,
} from '@mui/material';
import wineNameService, { WineColor, WineName } from '../../services/wineNameService';
import wineService, { parseVintageInput, Wine } from '../../services/wineService';
import {
  useWineIdentityForm, WineIdentityFields, EntityOps,
} from '../wine/WineIdentityForm';

/**
 * The venue-side Create New Wine dialog. The FORM ITSELF (pickers, field
 * order, validation, duplicate semantics) lives in
 * components/wine/WineIdentityForm and is shared with the operator console —
 * this file keeps the venue-specific parts: the dialog shell, on-the-fly
 * entity creation flagged needsReview, and the wineName+wine submit that
 * lands both as Needs Review in the admin queue.
 */

/**
 * The backend rejects a duplicate wine-name identity outright (unique index
 * on producer + region + specialName + labelGrapesKey). Contract: 409 with
 * code DUPLICATE_WINENAME, optionally carrying `existing`. Until that ships
 * the same rejection arrives as a 500 with the raw E11000 — treat both as
 * the same case.
 */
const isDuplicateWineNameError = (err: any): boolean => {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (status === 409) return true;
  if (status === 500) {
    const text = typeof body === 'string' ? body : JSON.stringify(body ?? {});
    return text.includes('E11000');
  }
  return false;
};

const DUPLICATE_WINENAME_COPY = 'This exact wine name already exists';

// The venue flow's on-the-fly creation: minimal records, needsReview: true —
// exactly the pre-extraction behaviour, now expressed through the seam.
const venueEntityOps: EntityOps = {
  createProducer: (name) => wineNameService.createProducerOnFly(name),
  createRegion: (name) => wineNameService.createRegionOnFly(name),
  createGrape: (name) => wineNameService.createGrapeOnFly(name),
};

interface Props {
  open: boolean;
  initialQuery: string;
  onCreated: (wine: Wine) => void;
  onClose: () => void;
  /**
   * Hand an existing wine name back to the picker so the user can pick one of
   * its vintage chips (or add a new year) instead of creating a duplicate.
   */
  onSelectExisting?: (wineName: WineName) => void;
}

const CreateWineModal: React.FC<Props> = ({ open, initialQuery, onCreated, onClose, onSelectExisting }) => {
  // A fresh key per open resets + prefills the shared form.
  const openSeq = useRef(0);
  const prevOpen = useRef(false);
  if (open && !prevOpen.current) openSeq.current++;
  useEffect(() => { prevOpen.current = open; }, [open]);

  const prefill = useMemo(() => ({ producerQuery: initialQuery }), [initialQuery]);
  const form = useWineIdentityForm({
    entityOps: venueEntityOps,
    prefillKey: open ? `open-${openSeq.current}` : null,
    prefill,
  });

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitBlockedByDuplicate, setSubmitBlockedByDuplicate] = useState(false);
  const [wineDuplicateWarning, setWineDuplicateWarning] = useState(false);

  // Editing the identity away from the rejected one clears the block.
  useEffect(() => {
    if (!form.wineNameDuplicate) setSubmitBlockedByDuplicate(false);
  }, [form.wineNameDuplicate]);

  const handleSubmit = async () => {
    if (!form.validate()) return;
    setSaving(true);
    setSubmitError('');
    setWineDuplicateWarning(false);

    try {
      // 1. Create WineName (needsReview: true)
      const wineName = await wineNameService.create({
        producerId: form.producer!._id,
        regionId: form.region!._id,
        labelGrapeIds: form.labelGrapes.map(g => g._id),
        specialName: form.specialName.trim() || undefined,
        color: form.color as WineColor,
        type: form.wineType,
        subType: form.subType || undefined,
        active: true,
        needsReview: true,
      });

      // 2. Parse vintage
      const parsed = parseVintageInput(form.vintageInput);

      // 3. Check Wine duplicate (warn only)
      try {
        const dupCheck = await wineService.checkDuplicate({
          wineNameId: wineName._id,
          vintageType: parsed.vintageType,
          vintage: parsed.vintage,
        });
        if (dupCheck.isDuplicate) setWineDuplicateWarning(true);
      } catch { /* ignore — non-blocking */ }

      // 4. Create Wine (needsReview: true)
      const wine = await wineService.create({
        wineNameId: wineName._id,
        vintageType: parsed.vintageType,
        vintage: parsed.vintage,
        baseVintage: parsed.baseVintage,
        active: true,
        needsReview: true,
      });

      onCreated(wine);
    } catch (err: any) {
      console.error('Failed to create wine:', err);
      if (isDuplicateWineNameError(err)) {
        // The identity was rejected by the unique index. Surface the same
        // banner + shortcut as the live check. The 409 contract may carry
        // `existing`; if it doesn't, look it up so the button still works.
        setSubmitBlockedByDuplicate(true);
        let existing: WineName | null = err?.response?.data?.existing ?? null;
        if (!existing) {
          try {
            const result = await wineNameService.checkDuplicate({
              producerId: form.producer!._id,
              regionId: form.region!._id,
              specialName: form.specialName.trim() || undefined,
              labelGrapeIds: form.labelGrapes.map(g => g._id),
            });
            existing = result.isDuplicate && result.existing ? result.existing : null;
          } catch { /* leave the banner buttonless rather than failing twice */ }
        }
        if (existing) form.setWineNameDuplicate(existing);
      } else {
        setSubmitError('Failed to create. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const isBusy = saving || form.creatingOnFly;
  // A known exact duplicate can only be rejected by the unique index — don't
  // let the user fire a request that cannot succeed.
  const blockedByDuplicate = Boolean(form.wineNameDuplicate) || submitBlockedByDuplicate;

  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Create New Wine</DialogTitle>

      <DialogContent dividers>
        {(submitError || form.opError) && (
          <Alert severity="error" sx={{ mb: 1 }}>{submitError || form.opError}</Alert>
        )}

        {form.wineNameDuplicate && (
          <Alert
            severity={submitBlockedByDuplicate ? 'error' : 'warning'}
            sx={{ mb: 1 }}
            action={onSelectExisting ? (
              <Button
                size="small"
                color="inherit"
                onClick={() => onSelectExisting(form.wineNameDuplicate!)}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Select existing wine
              </Button>
            ) : undefined}
          >
            {DUPLICATE_WINENAME_COPY}: <strong>{form.wineNameDuplicate.displayName}</strong>.
            Pick it instead and choose or add a vintage.
          </Alert>
        )}
        {submitBlockedByDuplicate && !form.wineNameDuplicate && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {DUPLICATE_WINENAME_COPY}. Close this form and pick it from the search results.
          </Alert>
        )}

        {wineDuplicateWarning && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            A wine with this name and vintage may already exist. It will be flagged for review.
          </Alert>
        )}

        <WineIdentityFields form={form} />

        <Alert severity="info" sx={{ mt: 2, fontSize: '0.78rem' }}>
          Both the Wine Name and Wine will be flagged <strong>Needs Review</strong> — visible in the admin review queue.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={isBusy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isBusy || blockedByDuplicate}
          sx={{ textTransform: 'none', fontWeight: 600, minWidth: 120 }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Create & Select'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateWineModal;
